class IngredientsSection {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      
      this.data = {
        ready: false,
        ingredients: [],
        filters: [],
        activeFilter: null,
        activeIngredient: null,
        showPreviewPanel: false,
        isTransitioning: false,
        sortDirection: 'ascending'
      };
  
      // Get section-specific elements
      this.elements = {
        filtersContainer: container.querySelector(`#filters-container-${this.sectionId}`),
        ingredientsGrid: container.querySelector(`#ingredients-grid-${this.sectionId}`),
        previewPanel: container.querySelector(`#preview-panel-${this.sectionId}`),
        sortSelect: container.querySelector('[data-sort-select]'),
        closePreview: container.querySelector(`#close-preview-${this.sectionId}`),
        activeImage: container.querySelector(`#active-image-${this.sectionId}`),
        activeTitle: container.querySelector(`#active-title-${this.sectionId}`),
        activeContent: container.querySelector(`#active-content-${this.sectionId}`),
        activeDetails: container.querySelector(`#active-details-${this.sectionId}`),
        relatedProducts: container.querySelector(`#related-products-${this.sectionId}`)
      };
  
      this.init();
    }
  
    init() {
      this.prepareIngredients();
      this.bindEvents();
      this.renderFilters();
      this.renderIngredients();
      this.addTaggedProducts();
      this.initAnalytics();
      
      this.data.ready = true;
      this.updateContainerClasses();
      
      console.info(`Ingredients section ${this.sectionId} initialized:`, this.data);
    }
  
    prepareIngredients() {
      const sectionData = window.ingredientsData && window.ingredientsData[this.sectionId];
      if (!sectionData) return;
  
      // Process ingredients data
      this.data.ingredients = sectionData.map(item => ({
        ...item,
        isActive: false
      }));
  
      // Extract unique filters from tags
      const allTags = this.data.ingredients.flatMap(ingredient => ingredient.tags);
      this.data.filters = [...new Set(allTags)].sort();
  
      // Initial sort A-Z
      this.sortIngredients('ascending');
    }
  
    bindEvents() {
      // Sort dropdown
      this.elements.sortSelect.addEventListener('change', (e) => {
        this.data.sortDirection = e.target.value;
        this.sortIngredients(e.target.value);
        this.trackSortEvent(e.target.value);
      });
  
      // Close preview panel
      this.elements.closePreview.addEventListener('click', () => {
        this.removeActiveIngredient();
      });
  
      // Close on outside click
      this.elements.previewPanel.addEventListener('click', (e) => {
        if (e.target === this.elements.previewPanel) {
          this.removeActiveIngredient();
        }
      });
  
      // Escape key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.data.showPreviewPanel) {
          this.removeActiveIngredient();
        }
      });
  
      // Delegate clicks for dynamic content
      this.elements.filtersContainer.addEventListener('click', (e) => {
        if (e.target.classList.contains('ingredients-filter')) {
          this.setActiveFilter(e.target.dataset.filter);
        }
      });
  
      this.elements.ingredientsGrid.addEventListener('click', (e) => {
        const ingredientEl = e.target.closest('.ingredient');
        if (ingredientEl) {
          // Remove focus to prevent blue outline flash on click
          ingredientEl.blur();
          const index = parseInt(ingredientEl.dataset.index);
          this.setActiveIngredient(index);
        }
      });

      // Add keyboard support for ingredients
      this.elements.ingredientsGrid.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const ingredientEl = e.target.closest('.ingredient');
          if (ingredientEl) {
            e.preventDefault();
            const index = parseInt(ingredientEl.dataset.index);
            this.setActiveIngredient(index);
          }
        }
      });
    }
  
    renderFilters() {
      const filtersHTML = this.data.filters.map(filter => `
        <div 
          class="ingredients-filter ${this.data.activeFilter === filter ? 'active' : ''}"
          data-filter="${filter}"
        >
          ${filter}
        </div>
      `).join('');
  
      this.elements.filtersContainer.innerHTML = filtersHTML;
    }
  
    renderIngredients() {
      // First render: Show text content immediately with empty image containers
      const ingredientsHTML = this.data.ingredients.map((ingredient, index) => {
        const isInFilter = !this.data.activeFilter || 
                          ingredient.tags.includes(this.data.activeFilter);
        
        return `
          <div 
            class="ingredient ${isInFilter ? 'in-filter' : ''} ${ingredient.isActive ? 'is-active' : ''}"
            data-name="${ingredient.title}"
            data-index="${index}"
            data-id="${ingredient.id}"
            tabindex="0"
            role="button"
            aria-label="View ${ingredient.title} details"
          >
            <div class="ingredient__image" data-image-src="${ingredient.image || ''}" data-image-alt="${ingredient.title}">
              <!-- Image will be loaded asynchronously -->
            </div>
            <div class="ingredient__title">${ingredient.title}</div>
          </div>
        `;
      }).join('');

      // Render text content immediately
      this.elements.ingredientsGrid.innerHTML = ingredientsHTML;
      this.elements.ingredientsGrid.classList.remove('loading');
      this.updateContainerClasses();
      
      // Then load images asynchronously
      this.loadImagesProgressively();
    }
  
    sortIngredients(direction) {
      if (direction === 'descending') {
        this.data.ingredients.reverse();
      } else {
        // Sort A-Z by title
        this.data.ingredients.sort((a, b) => a.title.localeCompare(b.title));
      }
      
      this.renderIngredients();
    }
  
    setActiveFilter(filter) {
      this.removeActiveIngredient();
  
      if (this.data.activeFilter === filter) {
        this.data.activeFilter = null;
      } else {
        this.data.activeFilter = filter;
      }
  
      this.renderFilters();
      this.renderIngredients();
      this.trackFilterEvent(filter);
    }
  
    setActiveIngredient(index) {
      const targetIngredient = this.data.ingredients[index];
      
      if (this.data.showPreviewPanel && 
          this.data.activeIngredient && 
          this.data.activeIngredient.id === targetIngredient.id) {
        this.removeActiveIngredient();
        return;
      }
  
      this.removeActiveIngredient();
      
      // Set active ingredient
      this.data.activeIngredient = { ...targetIngredient };
      this.data.ingredients[index].isActive = true;
      this.data.showPreviewPanel = true;
  
      // Update UI
      this.renderIngredients();
      this.renderActiveIngredient();
      this.updateContainerClasses();
  
      // Load related products
      this.setRelatedProducts(this.data.activeIngredient);
  
      // Analytics
      this.trackIngredientClick(targetIngredient.title, index);
    }
  
    removeActiveIngredient() {
      // Clear active states
      this.data.ingredients.forEach(ingredient => ingredient.isActive = false);
      this.data.activeIngredient = null;
      this.data.showPreviewPanel = false;
  
      // Update UI
      this.renderIngredients();
      this.elements.previewPanel.style.display = 'none';
      this.elements.previewPanel.classList.remove('active');
      this.updateContainerClasses();
    }
  
    renderActiveIngredient() {
      if (!this.data.activeIngredient) return;
  
      const ingredient = this.data.activeIngredient;
  
      // Update preview panel content
      this.elements.activeImage.innerHTML = ingredient.image ? 
        `<img src="${ingredient.image}" alt="${ingredient.title}" width="225" height="auto">` : '';
      
      this.elements.activeTitle.innerHTML = ingredient.title;
      this.elements.activeContent.innerHTML = ingredient.content || '';
  
      // Render details (FORMAT, FOUND, FUNCTION)
      if (ingredient.details && ingredient.details.length) {
        const detailsHTML = ingredient.details
          .filter(detail => detail.value)
          .map(detail => `
            <div class="active-ingredient-detail">
              <div class="active-ingredient-detail__title">${detail.title}</div>
              <div class="active-ingredient-detail__value">${detail.value}</div>
            </div>
          `).join('');
        
        this.elements.activeDetails.innerHTML = detailsHTML;
      }
  
      // Show panel with animation
      this.elements.previewPanel.style.display = 'block';
      setTimeout(() => {
        this.elements.previewPanel.classList.add('active');
      }, 10);
    }
  
    updateContainerClasses() {
      this.container.classList.toggle('filters-active', !!this.data.activeFilter);
      this.container.classList.toggle('ingredient-active', this.data.showPreviewPanel);
      this.container.classList.toggle('is-transitioning', this.data.isTransitioning);
    }

    // Load images progressively after text content is rendered
    loadImagesProgressively() {
      const imageContainers = this.elements.ingredientsGrid.querySelectorAll('.ingredient__image[data-image-src]');
      
      imageContainers.forEach((container, index) => {
        const imageSrc = container.dataset.imageSrc;
        const imageAlt = container.dataset.imageAlt;
        
        if (!imageSrc) return;
        
        // Add a small delay to stagger image loading for better UX
        setTimeout(() => {
          const img = new Image();
          
          img.onload = () => {
            // Create the img element with proper attributes
            const imgElement = document.createElement('img');
            imgElement.src = imageSrc;
            imgElement.alt = imageAlt;
            imgElement.loading = 'lazy';
            imgElement.width = 225;
            imgElement.height = 225;
            
            // Add fade-in effect
            imgElement.style.opacity = '0';
            imgElement.style.transition = 'opacity 0.3s ease';
            
            container.appendChild(imgElement);
            
            // Trigger fade-in
            requestAnimationFrame(() => {
              imgElement.style.opacity = '1';
            });
          };
          
          img.onerror = () => {
            // Handle broken images gracefully
            container.innerHTML = `<div class="ingredient__image-placeholder">${imageAlt}</div>`;
          };
          
          // Start loading the image
          img.src = imageSrc;
        }, index * 50); // Stagger by 50ms per image
      });
    }
  
    // Related products functionality
    async addTaggedProducts() {
      for (let ingredient of this.data.ingredients) {
        if (ingredient.relatedProductsTag) {
          try {
            const products = await this.getTaggedProducts(ingredient.relatedProductsTag);
            ingredient.relatedTaggedProducts = products;
          } catch (error) {
            console.error('Error loading tagged products:', error);
          }
        }
      }
    }
  
    async getTaggedProducts(tag) {
      try {
        const response = await fetch(`/collections/all/${tag}?view=data`);
        if (!response.ok) throw new Error('Network response was not ok');
        
        const data = await response.json();
        
        return data.map(item => ({
          id: item.id,
          title: item.title,
          url: item.url,
          image: item.image,
          price: item.price
        }));
      } catch (error) {
        console.error('Error fetching tagged products:', error);
        return [];
      }
    }
  
    setRelatedProducts(ingredient) {
      if (!ingredient.relatedTaggedProducts || !ingredient.relatedTaggedProducts.length) {
        this.elements.relatedProducts.innerHTML = '';
        return;
      }
  
      const productsHTML = `
        <div class="active-ingredient__slideshow-wrapper">
          <h3 class="active-ingredient__slideshow-title">Found in these products:</h3>
          <div class="related-products-grid">
            ${ingredient.relatedTaggedProducts.slice(0, 4).map(product => `
              <div class="related-product">
                <a href="${product.url}" class="related-product__link">
                  ${product.image ? `<img src="${product.image}" alt="${product.title}" loading="lazy" width="150" height="120">` : ''}
                  <h4 class="related-product__title">${product.title}</h4>
                  ${product.price ? `<div class="related-product__price">${product.price}</div>` : ''}
                </a>
              </div>
            `).join('')}
          </div>
        </div>
      `;
  
      this.elements.relatedProducts.innerHTML = productsHTML;
    }
  
    // Analytics methods
    initAnalytics() {
      this.trackEvent('Ingredients Section', 'Initialize', this.sectionId, {
        total_ingredients: this.data.ingredients.length,
        initial_sort: this.data.sortDirection
      });
    }
  
    trackSortEvent(sortDirection) {
      this.trackEvent('Ingredients', 'Sort', sortDirection, this.data.ingredients.length);
    }
  
    trackFilterEvent(filter) {
      this.trackEvent('Ingredients', 'Filter', filter || 'Clear Filter');
    }
  
    trackIngredientClick(ingredientName, ingredientIndex) {
      this.trackEvent('Ingredient', 'Click', ingredientName, ingredientIndex + 1);
    }
  
    trackEvent(category, action, label, value) {
      // Google Analytics 4
      if (typeof gtag !== 'undefined') {
        gtag('event', action.toLowerCase().replace(' ', '_'), {
          event_category: category,
          event_label: label,
          value: value,
          section_id: this.sectionId
        });
      }
  
      // Universal Analytics
      if (typeof ga !== 'undefined') {
        ga('send', 'event', category, action, label, value);
      }
  
      // Console log for debugging
      console.log(`Analytics: ${category} - ${action} - ${label}`, value);
    }
  }
  
  // Initialize sections when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    const ingredientsContainers = document.querySelectorAll('[data-section-type="ingredients"]');
    
    ingredientsContainers.forEach(container => {
      new IngredientsSection(container);
    });
  });
  
  // Register with Shopify sections (if using section groups)
  if (typeof window.Shopify !== 'undefined' && window.Shopify.theme && window.Shopify.theme.sections) {
    window.Shopify.theme.sections.register('ingredients', IngredientsSection);
  }